import type { TCacheOptions, TFetchCache, TCacheEntry } from '@casumo/fabric-fundamentals';

export abstract class BaseCache implements TFetchCache {
    protected readonly DEFAULT_TTL = 300_000; // 5 minutes

    abstract setStorage(key: string, entry: TCacheEntry): Promise<void>;
    abstract getStorage(key: string): Promise<TCacheEntry | undefined>;
    abstract deleteStorage(key: string): Promise<void>;
    abstract clearStorage(): Promise<void>;
    abstract getAllEntries(): Promise<[string, TCacheEntry][]>;

    async set(
        key: string,
        responsePromise: Promise<Response>,
        options: TCacheOptions = {}
    ): Promise<undefined> {
        const { ttl = this.DEFAULT_TTL, freshness, tags } = options;

        try {
            const response = await responsePromise;
            if (!response.ok) return;

            const clonedResponse = response.clone();
            const data = await clonedResponse.json();
            const now = Date.now();

            const entry: TCacheEntry = {
                data,
                expiryDate: now + ttl,
                ...(freshness ? { freshUntil: now + freshness } : undefined),
                ...(tags ? { tags } : undefined)
            };

            await this.setStorage(key, entry);
        } catch (error) {
            console.error(`Failed to cache response for key: ${key}.`, error);
        }
    }

    async get(key: string): Promise<TCacheEntry | undefined> {
        const entry = await this.getStorage(key);

        if (!entry) return undefined;

        if (Date.now() > entry.expiryDate) {
            await this.deleteStorage(key);
            return undefined;
        }

        return entry;
    }

    async has(key: string): Promise<boolean> {
        const entry = await this.getStorage(key);

        if (!entry) return false;

        if (Date.now() > entry.expiryDate) {
            await this.deleteStorage(key);
            return false;
        }

        return true;
    }

    async invalidateByKey(key: string): Promise<undefined> {
        await this.deleteStorage(key);
    }

    async invalidateByTag(tag: string): Promise<undefined> {
        const entries = await this.getAllEntries();

        for (const [key, entry] of entries) {
            if (entry.tags?.includes(tag)) {
                await this.deleteStorage(key);
            }
        }
    }

    async invalidateAll(): Promise<undefined> {
        await this.clearStorage();
    }
}
